if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          false
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

document.addEventListener("DOMContentLoaded", () => {
  const radios = document.querySelectorAll(".plan-radio");


  if (!radios.length) return;

  function findVariantSelect() {
    return document.querySelector('variant-selects select') || 
           document.querySelector('select[name^="options"]') || 
           document.querySelector('select[name="options[Select a Pack]"]');
  }

  radios.forEach(radio => {
    radio.addEventListener("change", function () {
      console.log("Radio changed →", this.value);

      const select = findVariantSelect();
      if (!select) return console.warn("❌ Select not found");

      const match = Array.from(select.options).find(opt => 
        opt.value.trim().toLowerCase() === this.value.trim().toLowerCase() || 
        opt.text.trim().toLowerCase() === this.value.trim().toLowerCase()
      );

      if (match) {
        select.value = match.value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
        console.log("✅ Variant updated to:", match.value);
      } else {
        console.warn("❌ No matching option in select for:", this.value);
      }

      // Highlight active card
      document.querySelectorAll(".plan-card").forEach(card => card.classList.remove("active"));
      radio.closest(".plan-card")?.classList.add("active");
    });
  });

  // Sync radios if select changes manually
  document.addEventListener("change", function (e) {
    const select = findVariantSelect();
    if (select && e.target === select) {
      radios.forEach(r => {
        const isMatch = (r.value.trim().toLowerCase() === e.target.value.trim().toLowerCase());
        r.checked = isMatch;
        r.closest('.plan-card')?.classList.toggle('active', isMatch);
      });
    }
  });

  // Loop through all radios on initial load
  const select = findVariantSelect();
  radios.forEach(r => {
    const card = r.closest('.plan-card');
    if (!card) return;

    if (select && r.value.trim().toLowerCase() === select.value.trim().toLowerCase()) {
      card.classList.add('active');
      r.checked = true;
    } else {
      card.classList.remove('active');
    }
  });

  // Desktop Mouse Drag-to-Scroll for .subscription-plans
  const planContainers = document.querySelectorAll(".subscription-plans");
  planContainers.forEach(slider => {
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let isDragging = false;

    slider.addEventListener("mousedown", (e) => {
      isDown = true;
      isDragging = false;
      slider.classList.add("is-dragging");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    window.addEventListener("mouseup", () => {
      if (isDown) {
        isDown = false;
        slider.classList.remove("is-dragging");
        setTimeout(() => { isDragging = false; }, 50);
      }
    });

    slider.addEventListener("mouseleave", () => {
      if (isDown) {
        isDown = false;
        slider.classList.remove("is-dragging");
      }
    });

    slider.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 1.5;
      if (Math.abs(x - startX) > 4) {
        isDragging = true;
      }
      slider.scrollLeft = scrollLeft - walk;
    });

    // Clicking on card selects it (unless dragged)
    slider.querySelectorAll(".plan-card").forEach(card => {
      card.addEventListener("click", (e) => {
        if (isDragging) {
          e.preventDefault();
          return;
        }
        const radio = card.querySelector(".plan-radio");
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    });
  });
});

/*
document.addEventListener("DOMContentLoaded", () => {
  const container = document.getElementById("ingredients-container");
  const dataEl = document.getElementById("product-ingredients");

  if (!container || !dataEl) return;

  const ingredients = JSON.parse(dataEl.textContent || "[]");

  container.innerHTML = ""; // clear demo HTML if any

  ingredients.forEach(item => {
    const html = `
      <div class="ingredient">
        <img src="${item.image}" alt="${item.name}" class="ingredient-img">
        <div class="ingredient-text">
          <h3 class="ingredient-name">${item.name}</h3>
          <p class="ingredient-desc">${item.desc}</p>
        </div>
      </div>
    `;
    container.insertAdjacentHTML("beforeend", html);
  });
});
*/

document.addEventListener("DOMContentLoaded", () => {
  const container = document.getElementById("usage-container");
  const dataEl = document.getElementById("product-usage");

  if (!container || !dataEl) return;

  const usage = JSON.parse(dataEl.textContent || "{}");

  let html = "";

  // When to use
  if (usage.when_to_use && usage.when_to_use.length) {
    html += `<div class="when-to-use">
      <h3>When to use</h3>
      <div class="when-items">`;

    usage.when_to_use.forEach((item, index) => {
      // select SVG based on item.icon
      let svg = "";
      if (item.icon === "sun") {
        svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" stroke="#4d7c0f" stroke-width="2" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="5"/>
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
        </svg>`;
      } else if (item.icon === "dinner") {
        svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" stroke="#4d7c0f" stroke-width="2" viewBox="0 0 24 24">
          <path d="M4 3h16M4 21h16M9 3v18M15 3v18"/>
        </svg>`;
      }

      // Add divider between items
      const divider = index > 0 ? `<div class="divider"></div>` : "";

      html += `
        ${divider}
        <div class="when-item">
          ${svg}
          <p>${item.text}</p>
        </div>`;
    });

    html += `</div></div>`;
  }

  // How to use
  if (usage.how_to_use && usage.how_to_use.length) {
    html += `<div class="how-to-use">
      <h3>How to use</h3>
      <ol>`;
    usage.how_to_use.forEach(step => {
      html += `<li>${step}</li>`;
    });
    html += `</ol></div>`;
  }

  // Note
  if (usage.note) {
    html += `<div class="usage-note">
      <h3>Note</h3>
      <p>${usage.note}</p>
    </div>`;
  }

  container.innerHTML = html;
});

document.addEventListener("DOMContentLoaded", () => {
  const container = document.getElementById("benefits-container");
  const dataEl = document.getElementById("product-benefits");

  if (!container || !dataEl) return;

  const benefits = JSON.parse(dataEl.textContent || "[]");

  container.innerHTML = "";

  benefits.forEach(item => {
    // let svg = "";

    // // Select SVG based on icon keyword
    // switch(item.icon) {
    //   case "blood":
    //     svg = `<svg style="border:1px solid; border-radius:20px; padding:2px;" xmlns="http://www.w3.org/2000/svg" width="38" height="38" fill="none" stroke="#444" stroke-width="2" viewBox="0 0 24 24">
    //       <path d="M12 3C12 3 6 9 6 13.5a6 6 0 0012 0C18 9 12 3 12 3z"/>
    //     </svg>`;
    //     break;
    //   case "weight":
    //     svg = `<svg style="border:1px solid; border-radius:20px; padding:2px;" xmlns="http://www.w3.org/2000/svg" width="38" height="38" fill="none" stroke="#444" stroke-width="2" viewBox="0 0 24 24">
    //       <circle cx="12" cy="12" r="10"/>
    //       <path d="M12 6v6l4 2"/>
    //     </svg>`;
    //     break;
    //   case "metabolism":
    //     svg = `<svg style="border:1px solid; border-radius:20px; padding:2px;" xmlns="http://www.w3.org/2000/svg" width="38" height="38" fill="none" stroke="#444" stroke-width="2" viewBox="0 0 24 24">
    //       <path d="M12 3c2.5 3 4 5.5 4 8a4 4 0 11-8 0c0-2.5 1.5-5 4-8z"/>
    //       <path d="M12 12v6"/>
    //     </svg>`;
    //     break;
    //   case "wellness":
    //     svg = `<svg style="border:1px solid; border-radius:20px; padding:2px;" xmlns="http://www.w3.org/2000/svg" width="38" height="38" fill="none" stroke="#444" stroke-width="2" viewBox="0 0 24 24">
    //       <circle cx="12" cy="12" r="10"/>
    //       <path d="M8 12h8M12 8v8"/>
    //     </svg>`;
    //     break;
    // }

    container.insertAdjacentHTML("beforeend", `
      <div class="benefit">
        <img src="${item.icon}" 
                   alt="${item.text}" 
                   width="38" height="38" 
                   style="border:1px solid; border-radius:20px; padding:2px;">
        <p>${item.text}</p>
      </div>
    `);
  });
});





document.addEventListener("DOMContentLoaded", () => {
  const container = document.getElementById("faq-container");
  const dataEl = document.getElementById("product-faqs");

  if (!container || !dataEl) return;

  const faqs = JSON.parse(dataEl.textContent || "[]");
  container.innerHTML = "";

  faqs.forEach(faq => {
    if (!faq.question || !faq.answer) return;

    const html = `
        <div class="faq-item">
          <button class="faq-question">
            ${faq.question}
            <span class="faq-icon">+</span>
          </button>
          <div class="faq-answer">
            <p>${faq.answer}</p>
          </div>
        </div>
      `;

    container.insertAdjacentHTML("beforeend", html);
  });

  // Add toggle functionality
  container.querySelectorAll(".faq-question").forEach(btn => {
    btn.addEventListener("click", () => {
      const item = btn.parentElement;
      item.classList.toggle("active");
    });
  });
});